import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { maintenanceItemsApi } from '../buildingManagementApi';
import type { MaintenanceRecordListItem } from '../buildingManagementTypes';
import { MAINTENANCE_DOCUMENT_MAX_BYTES, MaintenanceDocumentInput } from './MaintenanceDocumentInput';

/**
 * Files — or replaces — the document on one activity.
 *
 * **Replacing supersedes, and it is said before the tick**: the record points at the new file,
 * and the old one stays in the Repository rather than being destroyed.
 */
export function AttachMaintenanceDocumentModal({
  open,
  itemId,
  record,
  onClose,
  onSaved,
}: {
  open: boolean;
  itemId: string;
  record: MaintenanceRecordListItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();

  const [file, setFile] = useState<File | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [percent, setPercent] = useState(0);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = file !== null && file.size <= MAINTENANCE_DOCUMENT_MAX_BYTES && confirmed && !saving;

  async function save() {
    if (!canSave || file === null) return;

    setSaving(true);
    setError(null);
    setPercent(0);

    try {
      await maintenanceItemsApi.attach(itemId, record.id, file, setPercent);
      toast.success(`Filed the document for ${record.activity} (${formatDateOnly(record.performedOn)}).`);
      onSaved();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not file the document.', 0));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Document for ${record.activity}`}
      size="lg"
      id="maintenance-document-modal"
      /*
        NO Autogenerate. The one field is a binary the server verifies by magic bytes — there is
        nothing it could write honestly, so no button renders.
      */
      confirm={{
        id: 'maintenance-document-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="maintenance-document-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="maintenance-document-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel={percent > 0 ? `Filing ${percent}%` : 'Submitting'}
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <MaintenanceDocumentInput
          id="maintenance-document-file"
          file={file}
          onChange={setFile}
          required
          disabled={saving}
        />

        {record.attachmentRepositoryItemId != null ? (
          <p className="text-xs text-slate-600">
            This record already has a document filed. Filing another replaces it as the record&apos;s
            document — the superseded file stays in the Repository.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
